import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputField from "../../../../components/InputField";
import SelectField from "../../../../components/SelectField";
import Button from "../../../../components/Button";
import BackButton from "../../../../components/BackButton";
import PersonIcon from "@mui/icons-material/Person";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import "../../Common/form.css";
import "../../Admin/MasterData/vendorform.css";
import api from "../../../../api/api";
import VendorOnboardingConfirmModal from "./VendorOnboardingConfirmModal";

const VendorOnboardingForm = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [documentErrors, setDocumentErrors] = useState({});
  const [uploadSuccess, setUploadSuccess] = useState({});

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // =========================================================
  // DOCUMENT LIST
  // =========================================================

  const documentList = [
    {
      name: "cin_certificate",
      label: "CIN Certificate",
      description: "Company incorporation certificate",
      documentType: "CIN_CERTIFICATE",
    },
    {
      name: "gstn",
      label: "GSTN",
      description: "GST registration certificate",
      documentType: "GSTN",
    },
    {
      name: "pan",
      label: "PAN",
      description: "Company PAN document",
      documentType: "PAN",
    },
    {
      name: "cancelled_cheque",
      label: "Cancelled Cheque",
      description: "Company bank account cheque",
      documentType: "CANCELLED_CHEQUE",
    },
    {
      name: "company_identification_certificate",
      label: "Company Identification Certificate",
      description: "Company identification document",
      documentType: "COMPANY_IDENTIFICATION_CERTIFICATE",
    },
  ];

  // =========================================================
  // VENDOR KIND OPTIONS
  // =========================================================

  const vendorKindOptions = [
    {
      value: "Vendor",
      label: "Vendor",
    },
    {
      value: "Service Provider",
      label: "Service Provider",
    },
    {
      value: "Vendor & Service Provider",
      label: "Vendor & Service Provider",
    },
    {
      value: "NVOCC",
      label: "NVOCC",
    },
    {
      value: "Consolidator",
      label: "Consolidator",
    },
    {
      value: "Carrier",
      label: "Carrier",
    },
    {
      value: "CHA",
      label: "CHA",
    },
    {
      value: "Agent",
      label: "Agent",
    },
  ];

  // =========================================================
  // SEGMENTS
  // =========================================================

  const segments = [
    {
      value: "FE",
      label: "FE",
    },
    {
      value: "LE",
      label: "LE",
    },
    {
      value: "LI",
      label: "LI",
    },
    {
      value: "FI",
      label: "FI",
    },
  ];

  // =========================================================
  // FORMIK
  // =========================================================

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      // -----------------------------------------------------
      // COMPANY
      // -----------------------------------------------------

      company_name: "",
      gst_tan_no: "",
      pan_no: "",
      company_cin_no: "",
      segment: [],
      vendor_kind: "",
      tds_percentage:"",

      // -----------------------------------------------------
      // ADDRESS
      // -----------------------------------------------------

      flat_house_building: "",
      area_street_sector: "",
      country: "",
      state: "",
      pincode: "",

      // -----------------------------------------------------
      // POC 1
      // -----------------------------------------------------

      poc1_full_name: "",
      poc1_designation: "",
      poc1_email: "",
      poc1_mobile: "",
      poc1_telephone: "",
      poc1_extension: "",

      // -----------------------------------------------------
      // POC 2
      // -----------------------------------------------------

      poc2_full_name: "",
      poc2_designation: "",
      poc2_email: "",
      poc2_mobile: "",
      poc2_telephone: "",
      poc2_extension: "",

      // -----------------------------------------------------
      // DOCUMENTS
      // -----------------------------------------------------

      cin_certificate: null,
      gstn: null,
      pan: null,
      cancelled_cheque: null,
      company_identification_certificate: null,
    },

    // =======================================================
    // VALIDATION
    // =======================================================

    validationSchema: Yup.object({
      // COMPANY

      company_name: Yup.string().trim().required("Company Name is required"),

      gst_tan_no: Yup.string().trim().required("GST/TAN No. is required"),

      pan_no: Yup.string().trim().required("PAN No. is required"),

      company_cin_no: Yup.string().trim().required("CIN number is required"),

      tds_percentage: Yup.string().trim().required("TDS is required"),
      segment: Yup.array()
        .of(Yup.string())
        .min(1, "At least one segment is required")
        .required("Segment is required"),

      vendor_kind: Yup.string().trim().required("Vendor Kind is required"),

      // ADDRESS

      flat_house_building: Yup.string()
        .trim()
        .required("Flat/House No./Building/Company/Apartment is required"),

      area_street_sector: Yup.string()
        .trim()
        .required("Area/Street/Sector/Town/Village is required"),

      country: Yup.string().trim().required("Country is required"),

      state: Yup.string().trim().required("State is required"),

      pincode: Yup.string()
        .required("Pincode is required")
        .matches(/^[0-9]{6}$/, "Pincode must contain exactly 6 digits"),

      // POC 1

      poc1_full_name: Yup.string()
        .trim()
        .required("POC-1 Full Name is required"),

      poc1_designation: Yup.string()
        .trim()
        .required("POC-1 Designation is required"),

      poc1_email: Yup.string()
        .trim()
        .email("Please enter a valid POC-1 email address")
        .required("POC-1 Email is required"),

      poc1_mobile: Yup.string()
        .trim()
        .required("POC-1 Mobile Number is required"),

      poc1_telephone: Yup.string()
        .trim()
        .required("POC-1 Telephone Number is required"),

      poc1_extension: Yup.string()
        .trim()
        .required("POC-1 Extension Number is required"),

      // POC 2

      poc2_full_name: Yup.string()
        .trim()
        .required("POC-2 Full Name is required"),

      poc2_designation: Yup.string()
        .trim()
        .required("POC-2 Designation is required"),

      poc2_email: Yup.string()
        .trim()
        .email("Please enter a valid POC-2 email address")
        .required("POC-2 Email is required"),

      poc2_mobile: Yup.string()
        .trim()
        .required("POC-2 Mobile Number is required"),

      poc2_telephone: Yup.string()
        .trim()
        .required("POC-2 Telephone Number is required"),

      poc2_extension: Yup.string()
        .trim()
        .required("POC-2 Extension Number is required"),
    }),

    // =======================================================
    // FORM SUBMIT
    // =======================================================

    onSubmit: async (values) => {
      setSubmitError("");

      // Save values temporarily.
      // Actual API call happens after confirmation.
      setPendingValues(values);

      setShowConfirm(true);
    },
  });

  // =========================================================
  // CONFIRM + API SUBMIT
  // =========================================================

  const handleConfirmSubmit = async () => {
    if (!pendingValues) {
      return;
    }

    try {
      setLoadingSubmit(true);
      setLoading(true);
      setSubmitError("");

      const values = pendingValues;

      // =====================================================
      // FORM DATA
      // =====================================================

      const formData = new FormData();

      // =====================================================
      // COMPANY DETAILS
      // =====================================================

      formData.append("name", values.company_name?.trim() || "");

      formData.append("pan_number", values.pan_no?.trim() || "");

      formData.append("gst_number", values.gst_tan_no?.trim() || "");

      // Backend currently expects this.
      formData.append("tds_percentage", "0");

      // Multiple segments => FE,LE,LI
      formData.append(
        "segment_type",
        Array.isArray(values.segment)
          ? values.segment.join(",")
          : values.segment || "",
      );

      formData.append("vendor_kind", values.vendor_kind?.trim() || "");

      formData.append("company_cin_no", values.company_cin_no?.trim() || "");

      // =====================================================
      // ADDRESS
      // =====================================================

      const location = [
        values.flat_house_building,
        values.area_street_sector,
        values.country,
        values.state,
        values.pincode,
      ]
        .filter(Boolean)
        .join(", ");

      // Existing backend location field
      formData.append("location", location);

      // Individual address DB columns
      formData.append("address_flat", values.flat_house_building?.trim() || "");

      formData.append("address_area", values.area_street_sector?.trim() || "");

      formData.append("address_country", values.country?.trim() || "");

      formData.append("address_state", values.state?.trim() || "");

      formData.append("address_pincode", values.pincode?.trim() || "");

      // =====================================================
      // POC 1
      // =====================================================

      const poc1 = {
        full_name: values.poc1_full_name?.trim() || "",

        designation: values.poc1_designation?.trim() || "",

        email: values.poc1_email?.trim() || "",

        mobile: values.poc1_mobile?.trim() || "",

        telephone: values.poc1_telephone?.trim() || "",

        extension: values.poc1_extension?.trim() || "",
      };

      formData.append("poc1", JSON.stringify(poc1));

      // =====================================================
      // POC 2
      // =====================================================

      const poc2 = {
        full_name: values.poc2_full_name?.trim() || "",

        designation: values.poc2_designation?.trim() || "",

        email: values.poc2_email?.trim() || "",

        mobile: values.poc2_mobile?.trim() || "",

        telephone: values.poc2_telephone?.trim() || "",

        extension: values.poc2_extension?.trim() || "",
      };

      formData.append("poc2", JSON.stringify(poc2));

      // =====================================================
      // DOCUMENTS
      // =====================================================

      documentList.forEach((doc) => {
        const file = values[doc.name];

        if (file instanceof File) {
          // Actual PDF
          formData.append("documents", file, file.name);

          // Matching document type
          formData.append("document_types", doc.documentType);
        }
      });

      // =====================================================
      // DEBUG
      // =====================================================

      console.log("========== VENDOR FORM DATA ==========");

      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(key, {
            name: value.name,
            type: value.type,
            size: value.size,
          });
        } else {
          console.log(key, value);
        }
      }

      console.log("======================================");

      // =====================================================
      // API CALL
      // =====================================================

      const response = await api.post("/master-data/vendor-master", formData);

      // =====================================================
      // SUCCESS
      // =====================================================

      if (response.data?.vendor_id) {
        setShowConfirm(false);
        setPendingValues(null);

        formik.resetForm();

        navigate("/master-data/vendor-master");

        return;
      }

      // =====================================================
      // API RETURNED WITHOUT VENDOR ID
      // =====================================================

      setSubmitError(response.data?.error || "Failed to save vendor.");
    } catch (err) {
      console.error("❌ Error submitting vendor:", err);

      // =====================================================
      // ERROR HANDLING
      // =====================================================

      if (err.response?.status === 409) {
        setSubmitError(
          err.response?.data?.error ||
            "A vendor with this GST number already exists.",
        );
      } else {
        setSubmitError(
          err.response?.data?.error ||
            err.response?.data?.details ||
            "Something went wrong while saving vendor.",
        );
      }
    } finally {
      setLoadingSubmit(false);
      setLoading(false);
    }
  };

  // =========================================================
  // DOCUMENT UPLOAD
  // =========================================================

  const handleDocumentUpload = (event, documentName) => {
    const file = event.currentTarget.files?.[0];

    if (!file) {
      return;
    }

    // Clear previous error
    setDocumentErrors((prev) => ({
      ...prev,
      [documentName]: "",
    }));

    // Hide previous success
    setUploadSuccess((prev) => ({
      ...prev,
      [documentName]: false,
    }));

    // =====================================================
    // PDF VALIDATION
    // =====================================================

    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      setDocumentErrors((prev) => ({
        ...prev,
        [documentName]: "Only PDF files are supported.",
      }));

      formik.setFieldValue(documentName, null);

      event.currentTarget.value = "";

      return;
    }

    // =====================================================
    // FILE SIZE
    // =====================================================

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      setDocumentErrors((prev) => ({
        ...prev,
        [documentName]: "PDF file size should not exceed 5 MB.",
      }));

      formik.setFieldValue(documentName, null);

      event.currentTarget.value = "";

      return;
    }

    // =====================================================
    // SAVE FILE
    // =====================================================

    formik.setFieldValue(documentName, file);

    setDocumentErrors((prev) => ({
      ...prev,
      [documentName]: "",
    }));

    setUploadSuccess((prev) => ({
      ...prev,
      [documentName]: true,
    }));

    // Hide success after 1 second
    setTimeout(() => {
      setUploadSuccess((prev) => ({
        ...prev,
        [documentName]: false,
      }));
    }, 1000);

    // Reset input
    event.currentTarget.value = "";
  };

  // =========================================================
  // VIEW DOCUMENT
  // =========================================================

  const handleViewDocument = (file) => {
    if (!file) {
      return;
    }

    try {
      const fileUrl = URL.createObjectURL(file);

      const link = document.createElement("a");

      link.href = fileUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(fileUrl);
      }, 60000);
    } catch (err) {
      console.error("Unable to preview PDF:", err);

      setSubmitError("Unable to preview this PDF.");
    }
  };

  // =========================================================
  // REMOVE DOCUMENT
  // =========================================================

  const handleRemoveDocument = (documentName) => {
    formik.setFieldValue(documentName, null);

    setDocumentErrors((prev) => ({
      ...prev,
      [documentName]: "",
    }));

    setUploadSuccess((prev) => ({
      ...prev,
      [documentName]: false,
    }));

    setSubmitError("");
  };

  // =========================================================
  // CLOSE CONFIRM MODAL
  // =========================================================

  const handleCloseConfirm = () => {
    if (loadingSubmit) {
      return;
    }

    setShowConfirm(false);
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      <BackButton />

      <div className="vendor-master-page">
        <form onSubmit={formik.handleSubmit} noValidate>
          {/* =================================================
              ERROR
          ================================================== */}

          {submitError && (
            <div className="alert alert-danger vendor-error" role="alert">
              {submitError}
            </div>
          )}

          {/* =================================================
              COMPANY DETAILS
          ================================================== */}

          <fieldset className="vendor-section mt-4">
            <legend className="vendor-section-legend">
              <span className="vendor-section-icon">
                <PersonIcon fontSize="small" />
              </span>

              <span className="vendor-section-title">Company Details</span>
            </legend>

            <p className="vendor-section-description">
              Enter company registration and address information.
            </p>

            <div className="vendor-section-body">
              <div className="row g-3 mb-3">
                {/* COMPANY NAME */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Company Name"
                    name="company_name"
                    value={formik.values.company_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter company name"
                    required
                    error={
                      formik.touched.company_name && formik.errors.company_name
                    }
                  />
                </div>

                {/* GST */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="GST/TAN No."
                    name="gst_tan_no"
                    value={formik.values.gst_tan_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter GST/TAN number"
                    required
                    error={
                      formik.touched.gst_tan_no && formik.errors.gst_tan_no
                    }
                  />
                </div>

                {/* PAN */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="PAN No."
                    name="pan_no"
                    value={formik.values.pan_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter PAN number"
                    required
                    error={formik.touched.pan_no && formik.errors.pan_no}
                  />
                </div>

                {/* VENDOR KIND */}

                <div className="col-12 col-md-6 col-lg-4">
                  <SelectField
                    label="Vendor Kind"
                    name="vendor_kind"
                    value={formik.values.vendor_kind}
                    options={vendorKindOptions}
                    onChange={(value) =>
                      formik.setFieldValue("vendor_kind", value)
                    }
                    placeholder="Select vendor kind"
                    required
                    searchable
                    error={
                      formik.touched.vendor_kind && formik.errors.vendor_kind
                    }
                  />
                </div>

                {/* SEGMENTS */}

                <div className="col-12 col-md-6 col-lg-4">
                  <SelectField
                    label="Segments"
                    name="segment"
                    options={segments}
                    value={formik.values.segment}
                    onChange={(value) => formik.setFieldValue("segment", value)}
                    placeholder="Select segments"
                    required
                    searchable
                    isMulti
                    error={formik.touched.segment && formik.errors.segment}
                  />
                </div>

                {/* CIN */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Company CIN No."
                    name="company_cin_no"
                    value={formik.values.company_cin_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter CIN number"
                    required
                    error={
                      formik.touched.company_cin_no &&
                      formik.errors.company_cin_no
                    }
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="TDS (%)"
                    name="tds_percentage"
                    type="number"
                    value={formik.values.vendor_tds_percentage}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter TDS percentage"
                  />
                </div>
              </div>

              {/* =================================================
                  REGISTERED ADDRESS
              ================================================== */}

              <div className="vendor-subsection">
                <div className="vendor-subsection-title">
                  Registered Address
                </div>

                <div className="row g-3">
                  {/* FLAT */}

                  <div className="col-12 col-lg-6">
                    <InputField
                      label="Flat/House No./Building/Company/Apartment"
                      name="flat_house_building"
                      value={formik.values.flat_house_building}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter address"
                      required
                      error={
                        formik.touched.flat_house_building &&
                        formik.errors.flat_house_building
                      }
                    />
                  </div>

                  {/* AREA */}

                  <div className="col-12 col-lg-6">
                    <InputField
                      label="Area/Street/Sector/Town/Village"
                      name="area_street_sector"
                      value={formik.values.area_street_sector}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter area/street/sector"
                      required
                      error={
                        formik.touched.area_street_sector &&
                        formik.errors.area_street_sector
                      }
                    />
                  </div>

                  {/* COUNTRY */}

                  <div className="col-12 col-md-4">
                    <InputField
                      label="Country"
                      name="country"
                      value={formik.values.country}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter country"
                      required
                      error={formik.touched.country && formik.errors.country}
                    />
                  </div>

                  {/* STATE */}

                  <div className="col-12 col-md-4">
                    <InputField
                      label="State"
                      name="state"
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter state"
                      required
                      error={formik.touched.state && formik.errors.state}
                    />
                  </div>

                  {/* PINCODE */}

                  <div className="col-12 col-md-4">
                    <InputField
                      label="Pincode"
                      name="pincode"
                      value={formik.values.pincode}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter pincode"
                      required
                      error={formik.touched.pincode && formik.errors.pincode}
                    />
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

          {/* =================================================
              CONTACT DETAILS
          ================================================== */}

          <fieldset className="vendor-section mt-4">
            <legend className="vendor-section-legend">
              <span className="vendor-section-icon">
                <ContactMailIcon fontSize="small" />
              </span>

              <span className="vendor-section-title">Contact Details</span>
            </legend>

            <p className="vendor-section-description">
              Add authorised and assigned points of contact.
            </p>

            <div className="vendor-section-body">
              {/* =================================================
                  POC 1
              ================================================== */}

              <div className="vendor-poc-header">
                <div className="vendor-poc-heading">
                  <span className="vendor-poc-label">POC - 1</span>

                  <span className="vendor-poc-badge authorised">
                    Authorised
                  </span>
                </div>
              </div>

              <div className="row g-3">
                {/* FULL NAME */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Full Name"
                    name="poc1_full_name"
                    value={formik.values.poc1_full_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter full name"
                    required
                    error={
                      formik.touched.poc1_full_name &&
                      formik.errors.poc1_full_name
                    }
                  />
                </div>

                {/* DESIGNATION */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Designation"
                    name="poc1_designation"
                    value={formik.values.poc1_designation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter designation"
                    required
                    error={
                      formik.touched.poc1_designation &&
                      formik.errors.poc1_designation
                    }
                  />
                </div>

                {/* EMAIL */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Mail ID"
                    name="poc1_email"
                    type="email"
                    value={formik.values.poc1_email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter email address"
                    required
                    error={
                      formik.touched.poc1_email && formik.errors.poc1_email
                    }
                  />
                </div>

                {/* MOBILE */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Mobile No."
                    name="poc1_mobile"
                    type="tel"
                    value={formik.values.poc1_mobile}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter mobile number"
                    required
                    error={
                      formik.touched.poc1_mobile && formik.errors.poc1_mobile
                    }
                  />
                </div>

                {/* TELEPHONE */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Telephone No."
                    name="poc1_telephone"
                    type="tel"
                    value={formik.values.poc1_telephone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter telephone number"
                    required
                    error={
                      formik.touched.poc1_telephone &&
                      formik.errors.poc1_telephone
                    }
                  />
                </div>

                {/* EXTENSION */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Extension No."
                    name="poc1_extension"
                    value={formik.values.poc1_extension}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter extension number"
                    required
                    error={
                      formik.touched.poc1_extension &&
                      formik.errors.poc1_extension
                    }
                  />
                </div>
              </div>

              {/* =================================================
                  POC 2
              ================================================== */}

              <div className="vendor-poc-header mt-4">
                <div className="vendor-poc-heading">
                  <span className="vendor-poc-label">POC - 2</span>

                  <span className="vendor-poc-badge assigned">Assigned</span>
                </div>
              </div>

              <div className="row g-3">
                {/* FULL NAME */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Full Name"
                    name="poc2_full_name"
                    value={formik.values.poc2_full_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter full name"
                    required
                    error={
                      formik.touched.poc2_full_name &&
                      formik.errors.poc2_full_name
                    }
                  />
                </div>

                {/* DESIGNATION */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Designation"
                    name="poc2_designation"
                    value={formik.values.poc2_designation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter designation"
                    required
                    error={
                      formik.touched.poc2_designation &&
                      formik.errors.poc2_designation
                    }
                  />
                </div>

                {/* EMAIL */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Mail ID"
                    name="poc2_email"
                    type="email"
                    value={formik.values.poc2_email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter email address"
                    required
                    error={
                      formik.touched.poc2_email && formik.errors.poc2_email
                    }
                  />
                </div>

                {/* MOBILE */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Mobile No."
                    name="poc2_mobile"
                    type="tel"
                    value={formik.values.poc2_mobile}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter mobile number"
                    required
                    error={
                      formik.touched.poc2_mobile && formik.errors.poc2_mobile
                    }
                  />
                </div>

                {/* TELEPHONE */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Telephone No."
                    name="poc2_telephone"
                    type="tel"
                    value={formik.values.poc2_telephone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter telephone number"
                    required
                    error={
                      formik.touched.poc2_telephone &&
                      formik.errors.poc2_telephone
                    }
                  />
                </div>

                {/* EXTENSION */}

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Extension No."
                    name="poc2_extension"
                    value={formik.values.poc2_extension}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter extension number"
                    required
                    error={
                      formik.touched.poc2_extension &&
                      formik.errors.poc2_extension
                    }
                  />
                </div>
              </div>
            </div>
          </fieldset>

          {/* =================================================
              DOCUMENTS
          ================================================== */}

          <fieldset className="vendor-section mt-4">
            <legend className="vendor-section-legend">
              <span className="vendor-section-icon">
                <ContactSupportIcon fontSize="small" />
              </span>

              <span className="vendor-section-title">
                Supporting Docs Checklist
              </span>
            </legend>

            <p className="vendor-section-description">
              Upload the required documents in PDF format.
            </p>

            <div className="vendor-section-body">
              <div className="row g-3 mb-3">
                {documentList.map((doc) => {
                  const uploadedFile = formik.values[doc.name];

                  const documentError = documentErrors[doc.name];

                  return (
                    <div className="col-12 col-md-6 col-lg-4" key={doc.name}>
                      <div
                        className={`vendor-document-upload-card ${
                          uploadedFile ? "uploaded" : ""
                        } ${documentError ? "has-error" : ""}`}
                      >
                        {/* INFO */}

                        <div className="vendor-document-info">
                          <div
                            className={`vendor-document-icon ${
                              uploadedFile
                                ? "success"
                                : documentError
                                  ? "error"
                                  : ""
                            }`}
                          >
                            {uploadedFile ? "✓" : "PDF"}
                          </div>

                          <div className="vendor-document-title">
                            <span>{doc.label}</span>

                            <small>{doc.description}</small>
                          </div>
                        </div>

                        {/* FILE INPUT */}

                        <input
                          type="file"
                          id={`document-${doc.name}`}
                          accept="application/pdf,.pdf"
                          style={{
                            display: "none",
                          }}
                          onChange={(event) =>
                            handleDocumentUpload(event, doc.name)
                          }
                        />

                        {/* ERROR */}

                        {documentError && (
                          <div className="vendor-document-error" role="alert">
                            {documentError}
                          </div>
                        )}

                        {/* SUCCESS */}

                        {uploadSuccess[doc.name] && (
                          <div className="vendor-uploaded-status">
                            <span>✓</span>
                            PDF uploaded successfully
                          </div>
                        )}

                        {/* ACTIONS */}

                        <div className="vendor-document-actions">
                          {!uploadedFile ? (
                            <label
                              htmlFor={`document-${doc.name}`}
                              className="vendor-upload-btn"
                            >
                              Upload PDF
                            </label>
                          ) : (
                            <>
                              {/* VIEW */}

                              <button
                                type="button"
                                className="vendor-view-btn"
                                onClick={() => handleViewDocument(uploadedFile)}
                              >
                                View
                              </button>

                              {/* REPLACE */}

                              <label
                                htmlFor={`document-${doc.name}`}
                                className="vendor-replace-btn"
                              >
                                Replace
                              </label>

                              {/* REMOVE */}

                              <button
                                type="button"
                                className="vendor-remove-btn"
                                onClick={() => handleRemoveDocument(doc.name)}
                              >
                                Remove
                              </button>
                            </>
                          )}
                        </div>

                        {/* FILE NAME */}

                        {uploadedFile && (
                          <div
                            className="vendor-file-name"
                            title={uploadedFile.name}
                          >
                            📄 {uploadedFile.name}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* =================================================
                SUBMIT
            ================================================== */}

            <div className="d-flex align-items-center justify-content-end py-2 me-3">
              <Button
                type="submit"
                variant="primary"
                text="Submit"
                loading={loading}
                disabled={loading}
              />
            </div>
          </fieldset>
        </form>
      </div>

      {/* =====================================================
          VENDOR ONBOARDING CONFIRM MODAL
      ====================================================== */}

      <VendorOnboardingConfirmModal
        show={showConfirm}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmSubmit}
        loading={loadingSubmit}
        vendorName={pendingValues?.company_name}
        vendorGst={pendingValues?.gst_tan_no}
        vendorPan={pendingValues?.pan_no}
      />
    </>
  );
};

export default VendorOnboardingForm;
